import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import LendingOperationsApi from "../../../../../infrastructure/api/lendingOperations";
import { isBarqLoginRequired } from "./BarqPortalOtpLoginModal";

const toLoanId = (row) => Number(row?.loanProcessId ?? row?.LoanProcessId ?? 0);
const toPhone = (row) =>
  String(
    row?.applicantMobile ??
      row?.ApplicantMobile ??
      row?.mobile ??
      row?.Mobile ??
      ""
  ).trim();
const toRef = (row) =>
  String(
    row?.requestTrackingCode ??
      row?.RequestTrackingCode ??
      row?.referenceId ??
      row?.ReferenceId ??
      ""
  ).trim();
const toAmount = (row) => {
  const raw = row?.requestedAmount ?? row?.RequestedAmount;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : "";
};

const downloadBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName || "shop-applicants.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Build/fill BarghPay shop-applicants template (phone + requested_amount)
 * and optionally upload to /admin/cpg-shops/{id}/applicants/import.
 */
const BarqApplicantsSubmitModal = ({
  open,
  onClose,
  source = "SolarentOperator",
  rows = [],
  onRequireLogin,
  onDone,
}) => {
  const pendingRows = useMemo(
    () =>
      (rows || []).filter((row) => {
        const tracking = String(row?.trackingCode ?? row?.TrackingCode ?? "").trim();
        return toLoanId(row) > 0 && !tracking;
      }),
    [rows]
  );

  const [amounts, setAmounts] = useState({});
  const [sendSms, setSendSms] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const next = {};
    pendingRows.forEach((row) => {
      const id = toLoanId(row);
      next[id] = toAmount(row);
    });
    setAmounts(next);
    setSendSms(false);
  }, [open, pendingRows]);

  const payload = () => {
    const loanProcessIds = pendingRows.map((r) => toLoanId(r));
    const amountItems = loanProcessIds
      .map((id) => ({
        LoanProcessId: id,
        RequestedAmount: Number(amounts[id] || 0),
      }))
      .filter((x) => x.RequestedAmount > 0);

    if (amountItems.length !== loanProcessIds.length) {
      throw new Error("برای همه ردیف‌ها مبلغ تسهیلات (ریال) را وارد کنید.");
    }

    return {
      LoanProcessIds: loanProcessIds,
      Amounts: amountItems,
      PendingOnly: false,
      SendSms: sendSms,
      SyncAfterImport: true,
    };
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      const body = payload();
      const blob = await LendingOperationsApi.exportBarqApplicantsExcel(source, body);
      if (!blob || !(blob instanceof Blob)) {
        throw new Error("دانلود فایل اکسل ناموفق بود.");
      }
      if (blob.type && blob.type.includes("application/json")) {
        const text = await blob.text();
        let msg = "دانلود فایل اکسل ناموفق بود.";
        try {
          const parsed = JSON.parse(text);
          msg = parsed?.message || parsed?.Message || msg;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      downloadBlob(blob, `shop-applicants-${Date.now()}.xlsx`);
      toast.success("فایل نمونه برق‌پی آماده شد.");
    } catch (err) {
      toast.error(err?.message || "ساخت فایل اکسل ناموفق بود.");
    } finally {
      setBusy(false);
    }
  };

  const handleUpload = async () => {
    setBusy(true);
    try {
      const body = payload();
      const res = await LendingOperationsApi.importBarqApplicants(source, body);
      if (isBarqLoginRequired(res)) {
        onRequireLogin?.();
        return;
      }
      if (res?.isSuccess === false) {
        throw new Error(res?.message || res?.Message || "آپلود به برق‌پی ناموفق بود.");
      }
      toast.success(res?.message || res?.Message || "فایل در برق‌پی آپلود شد.");
      onDone?.();
      onClose?.();
    } catch (err) {
      if (isBarqLoginRequired(err)) {
        onRequireLogin?.();
        return;
      }
      toast.error(err?.message || "آپلود به برق‌پی ناموفق بود.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose?.()} fullWidth maxWidth="sm">
      <DialogTitle>ارسال متقاضیان به برق‌پی (اکسل)</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            قالب برق‌پی فقط دو ستون دارد: <code>phone</code> و{" "}
            <code>requested_amount</code>. فایل ساخته و در پنل آپلود می‌شود.
          </Alert>

          {pendingRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              پرونده معلقی بدون کد پیگیری برق‌پی در صف فعلی نیست.
            </Typography>
          ) : (
            pendingRows.map((row) => {
              const id = toLoanId(row);
              return (
                <Box
                  key={id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1.25,
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2">
                    {toRef(row) || `وام ${id}`}
                    <br />
                    <Typography component="span" variant="caption" color="text.secondary">
                      {toPhone(row) || "بدون موبایل"}
                    </Typography>
                  </Typography>
                  <TextField
                    size="small"
                    label="مبلغ (ریال)"
                    type="number"
                    value={amounts[id] ?? ""}
                    onChange={(e) =>
                      setAmounts((prev) => ({ ...prev, [id]: e.target.value }))
                    }
                    disabled={busy}
                  />
                </Box>
              );
            })
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={sendSms}
                onChange={(e) => setSendSms(e.target.checked)}
                disabled={busy}
              />
            }
            label="ارسال پیامک به متقاضیان در برق‌پی"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          انصراف
        </Button>
        <Button
          variant="outlined"
          onClick={handleDownload}
          disabled={busy || pendingRows.length === 0}
        >
          دانلود اکسل
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={busy || pendingRows.length === 0}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
        >
          آپلود در برق‌پی
        </Button>
      </DialogActions>
    </Dialog>
  );
};

BarqApplicantsSubmitModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  source: PropTypes.string,
  rows: PropTypes.array,
  onRequireLogin: PropTypes.func,
  onDone: PropTypes.func,
};

export default BarqApplicantsSubmitModal;
